import React, { useEffect, useState } from "react";
import axios from "axios";
import dayjs from "dayjs";
import { useUserAuth } from "../context/UserAuthContext";
import { Button } from "react-bootstrap";

const VenueBookings = () => {
  const base_url = import.meta.env.VITE_LOCAL_BASE_URL;
  const { user, language, token } = useUserAuth();
  const merchant_id = user.uid;
  const [data, setData] = useState([]);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [vendors, setVendors] = useState([]);

  const fetchVendors = async () => {
    try {
      const res = await axios.get(`${base_url}/api/v1/manager/vendors`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setVendors(res.data.vendors);
    } catch (err) {
      console.log("Error fetching vendors:", err);
    }
  };

  const getEnquireList = async () => {
    const response = await axios.get(
      `${base_url}/api/v1/manager/venue-bookings`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: {
          vendor_id: selectedVendor || undefined,
        },
      },
    );
    setData(response.data.bookings);
  };

  useEffect(() => {
    if (!merchant_id) return;
    getEnquireList();
    fetchVendors();
  }, [token, selectedVendor]);

  const maskString = (str) => {
    if (!str) return "";
    if (str.length <= 2) return str;
    return "X".repeat(str.length - 2) + str.slice(-2);
  };

  // Function to get payment status badge
  const getPaymentStatusBadge = (status) => {
    if (status) {
      return {
        class: "bg-success",
        text: language === "en" ? "Paid" : "भुगतान हुआ",
      };
    }
    return {
      class: "bg-warning",
      text: language === "en" ? "Pending" : "लंबित",
    };
  };

  // Function to get event type badge
  const getEventTypeBadge = (type) => {
    const types = {
      "wedding": { class: "bg-danger" },
      "corporate": { class: "bg-primary" },
      "birthday": { class: "bg-info" },
      "anniversary": { class: "bg-purple" },
      "conference": { class: "bg-secondary" },
    };
    const defaultType = { class: "bg-secondary" };
    const matched = types[type?.toLowerCase()] || defaultType;
    return { class: matched.class };
  };

  return (
    <div className="page-wrapper">
      <div className="container-fluid">
        <div className="row">
          <div className="col-md-12">
            <div className="card shadow-lg border-0 rounded-3">
              <div className="card-body p-4">
                <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
                  <h2 className="mb-0 fw-bold" style={{ color: '#2c3e50' }}>
                    {language === "en" ? "Venue Bookings" : "स्थल बुकिंग"}
                  </h2>
                  <div className="d-flex align-items-center gap-2">
                    <label className="fw-semibold text-muted">
                      {language === "en" ? "Filter by Vendor:" : "विक्रेता द्वारा फ़िल्टर करें:"}
                    </label>
                    <select
                      className="form-select rounded-pill px-3 py-2"
                      value={selectedVendor || ""}
                      onChange={(e) => setSelectedVendor(e.target.value || null)}
                      style={{ 
                        width: "220px", 
                        borderColor: '#dee2e6',
                        backgroundColor: '#f8f9fa',
                        fontWeight: '500'
                      }}
                    >
                      <option value="">{language === "en" ? "All Vendors" : "सभी विक्रेता"}</option>
                      {vendors.map((v) => (
                        <option key={v.id} value={v.id}>
                          {v.business_name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="table-responsive">
                  <table className="table table-hover table-bordered shadow-sm rounded-3 overflow-hidden">
                    <thead style={{ backgroundColor: '#f8f9fa' }}>
                      <tr className="text-center">
                        <th style={{ width: '60px', color: '#495057', fontWeight: '600' }}>#</th>
                        <th style={{ color: '#495057', fontWeight: '600' }}>Vendor Name</th>
                        <th style={{ width: '110px', color: '#495057', fontWeight: '600' }}>Event Date</th>
                        <th style={{ width: '100px', color: '#495057', fontWeight: '600' }}>Number of Pax</th>
                        <th style={{ width: '130px', color: '#495057', fontWeight: '600' }}>Room Requirement</th>
                        <th style={{ width: '120px', color: '#495057', fontWeight: '600' }}>Event Type</th>
                        <th style={{ color: '#495057', fontWeight: '600' }}>Name</th>
                        <th style={{ width: '130px', color: '#495057', fontWeight: '600' }}>Mobile Number</th>
                        <th style={{ width: '130px', color: '#495057', fontWeight: '600' }}>Booking Venue Date</th>
                        <th style={{ width: '120px', color: '#495057', fontWeight: '600' }}>Payment Status</th>
                       </tr>
                    </thead>
                    <tbody>
                      {data.length > 0 ? (
                        data.map((item, index) => {
                          const paymentStatus = getPaymentStatusBadge(item.payment_status);
                          const eventType = getEventTypeBadge(item.event_type);
                          
                          return (
                            <tr key={index} className="align-middle" style={{ transition: 'all 0.3s ease' }}>
                              <td className="text-center fw-semibold">{index + 1}</td>
                              <td>
                                <span className="fw-medium">{item.vendor_name}</span>
                              </td>
                              <td className="text-center">
                                <span className="badge bg-light text-dark px-2 py-1 rounded-pill">
                                  {dayjs(item.event_date).format("DD MMM, YYYY")}
                                </span>
                              </td>
                              <td className="text-center">
                                <strong>{item.no_of_packs}</strong>
                              </td>
                              <td>{item.room_requirement || "—"}</td>
                              <td className="text-center">
                                <span className={`badge ${eventType.class} px-2 py-1 rounded-pill`}>
                                  {item.event_type || "—"}
                                </span>
                              </td>
                              <td>
                                <strong>{item.name}</strong>
                              </td>
                              <td className="text-center">
                                <span className="font-monospace">
                                  {item.payment_status
                                    ? item.mob_no
                                    : maskString(item.mob_no)}
                                </span>
                              </td>
                              <td className="text-center">
                                <small className="text-muted">
                                  {dayjs(item.created_at).format("DD MMM, YYYY")}
                                </small>
                              </td>
                              <td className="text-center">
                                <span
                                  className={`badge ${paymentStatus.class} px-3 py-2 rounded-pill fs-7`}
                                  style={{ fontSize: '0.75rem', fontWeight: '500' }}
                                >
                                  {paymentStatus.text}
                                </span>
                              </td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan="10" className="text-center py-5">
                            <div className="text-muted">
                              <p className="mt-2 mb-0">
                                {language === "en" 
                                  ? "No bookings found" 
                                  : "कोई बुकिंग नहीं मिली"}
                              </p>
                              <small className="text-muted">
                                {language === "en"
                                  ? "Try changing the vendor filter"
                                  : "विक्रेता फ़िल्टर बदलने का प्रयास करें"}
                              </small>
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {data.length > 0 && (
                  <div className="mt-3 d-flex justify-content-between align-items-center flex-wrap gap-2">
                    <div className="text-muted">
                      <small>
                        {language === "en" 
                          ? `Showing ${data.length} booking${data.length > 1 ? 's' : ''}` 
                          : `${data.length} बुकिंग${data.length > 1 ? 'एं' : ''} दिखा रहे हैं`}
                      </small>
                    </div>
                    <div className="d-flex gap-2">
                      <span className="badge bg-success px-2 py-1 rounded-pill">
                        {language === "en" ? "Paid" : "भुगतान हुआ"}
                      </span>
                      <span className="badge bg-warning px-2 py-1 rounded-pill">
                        {language === "en" ? "Pending" : "लंबित"}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .table-hover tbody tr:hover {
          background-color: #f8f9fa;
          transform: scale(1.01);
          transition: all 0.3s ease;
          cursor: pointer;
        }
        
        .table {
          margin-bottom: 0;
        }
        
        .form-select:focus {
          border-color: #86b7fe;
          box-shadow: 0 0 0 0.25rem rgba(13, 110, 253, 0.25);
        }
        
        .badge {
          font-weight: 500;
          letter-spacing: 0.3px;
        }
        
        .bg-purple {
          background-color: #6f42c1;
          color: white;
        }
        
        @media (max-width: 768px) {
          .d-flex {
            flex-direction: column;
            align-items: flex-start !important;
          }
          
          .table th, .table td {
            font-size: 0.75rem;
            padding: 0.5rem;
          }
          
          .badge {
            font-size: 0.7rem;
          }
        }
        
        @media (max-width: 1200px) {
          .table th, .table td {
            white-space: nowrap;
          }
        }
      `}</style>
    </div>
  );
};

export default VenueBookings;